/**
 * Phase 8 · C-49.3 — Requisition right-rail dashboard stack.
 *
 * Six live mini-viz cards + a recent-activity feed + demoted text CTAs,
 * replacing the legacy thin-strip RecordMetaCard per §34.3.C:
 *
 *   1. TatGauge                — days-used / target gauge
 *   2. ApplicationFunnel       — 4-stage funnel with click-through
 *   3. AppsByDay MiniSparkline — last 14 days of applications
 *   4. TimeToFirstInterview    — median days + trend vs prior cohort
 *   5. ChannelMixBar           — sourcing channel rollup
 *   6. Recent activity         — 5 latest object-history rows
 *   + text links: View history · View full timeline
 */
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, History } from 'lucide-react';
import type { Requisition } from '@core/types';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import {
  ApplicationFunnel,
  TatGauge,
  MiniSparkline,
  ChannelMixBar,
  type ApplicationFunnelCounts,
  type FunnelStage,
} from '@shared/components/viz';
import {
  requisitionDashboardService,
  type ChannelKey,
  type TimeToFirstInterviewStat,
} from '@core/supabase/services/requisitionDashboardService';

type ActivityRow = {
  id: string;
  attribute_name: string;
  old_value: string | null;
  new_value: string | null;
  action: 'insert' | 'update' | 'delete';
  created_at: string;
  changed_by_org_user_id: string | null;
};

interface DashboardData {
  funnel: ApplicationFunnelCounts;
  appsPerDay: number[];
  ttfi: TimeToFirstInterviewStat;
  channelMix: Record<ChannelKey, number>;
  activity: ActivityRow[];
}

const EMPTY: DashboardData = {
  funnel: { applied: 0, shortlisted: 0, interviewing: 0, hired: 0 },
  appsPerDay: [],
  ttfi: { medianDays: 0, trendPct: 0, sampleSize: 0 },
  channelMix: { internal: 0, linkedin: 0, naukri: 0, peerlist: 0, other: 0 },
  activity: [],
};

function Card({ title, children, dense = false }: { title: string; children: React.ReactNode; dense?: boolean }) {
  return (
    <section
      className="rounded-lg border"
      style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)' }}
    >
      <div className="border-b px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary"
        style={{ borderColor: 'var(--vscode-border-primary)' }}>
        {title}
      </div>
      <div className={dense ? 'px-3 py-2' : 'p-3'}>{children}</div>
    </section>
  );
}

function computeTatFromReq(req: Requisition): { days: number; target: number; breach: boolean } {
  if (!req.opened_at) return { days: 0, target: 0, breach: false };
  const now = Date.now();
  const opened = new Date(req.opened_at).getTime();
  const days = Math.max(0, Math.round((now - opened) / 86_400_000));
  const target = req.target_close_date
    ? Math.max(1, Math.round((new Date(req.target_close_date).getTime() - opened) / 86_400_000))
    : 30;
  const breach = Boolean(req.target_close_date)
    && !req.closed_at
    && now > new Date(req.target_close_date!).getTime();
  return { days, target, breach };
}

function formatActivity(row: ActivityRow): string {
  const attr = row.attribute_name.replace(/_/g, ' ');
  if (row.action === 'insert') return `Created ${attr}`;
  if (row.action === 'delete') return `Removed ${attr}`;
  if (row.old_value != null && row.new_value != null) {
    return `${attr}: ${row.old_value} → ${row.new_value}`;
  }
  return `Updated ${attr}`;
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export interface RequisitionDashboardStackProps {
  req: Requisition;
  orgId: string;
  onViewTimeline?: () => void;
}

export function RequisitionDashboardStack({ req, orgId, onViewTimeline }: RequisitionDashboardStackProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData>(EMPTY);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [funnel, appsPerDay, ttfi, channelMix, activity] = await Promise.all([
          requisitionDashboardService.getFunnel(orgId, req.id),
          requisitionDashboardService.getAppsPerDay(orgId, req.id, 14),
          requisitionDashboardService.getTimeToFirstInterview(orgId, req.id),
          requisitionDashboardService.getChannelMix(orgId, req.id),
          requisitionDashboardService.getRecentActivity(orgId, req.id, 5),
        ]);
        if (!cancelled) setData({ funnel, appsPerDay, ttfi, channelMix, activity });
      } catch (err) {
        // Keep the EMPTY state so cards still render their zero-states. Surface via console in dev.
        if (import.meta.env.DEV) console.error('[RequisitionDashboardStack] load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, req.id]);

  const onFunnelClick = useCallback((stage: FunnelStage) => {
    navigate(`/applications?requisition_id=${encodeURIComponent(req.id)}&stage=${stage}`);
  }, [navigate, req.id]);

  const tat = computeTatFromReq(req);
  const trendDown = data.ttfi.trendPct < 0;
  const trendUp   = data.ttfi.trendPct > 0;

  return (
    <div
      className="flex flex-col gap-3"
      aria-busy={loading}
      aria-label="Requisition metrics dashboard"
    >
      <Card title="Turn-around">
        {tat.target > 0 ? (
          <div className="flex justify-center">
            <TatGauge days={tat.days} target={tat.target} breach={tat.breach} />
          </div>
        ) : (
          <p className="text-[12px] vscode-text-tertiary">
            TAT tracking starts once the requisition moves to <span className="font-medium">sourcing</span>.
          </p>
        )}
      </Card>

      <Card title="Application funnel" dense>
        <ApplicationFunnel counts={data.funnel} onClickStage={onFunnelClick} />
      </Card>

      <Card title="Applications · last 14 d" dense>
        <MiniSparkline
          data={data.appsPerDay}
          tone="success"
          ariaLabel={`Applications per day for the trailing 14 days, latest ${data.appsPerDay[data.appsPerDay.length - 1] ?? 0}`}
        />
        <div className="mt-1 flex items-center justify-between text-[11px] vscode-text-tertiary">
          <span>{data.appsPerDay.reduce((a, b) => a + b, 0)} total</span>
          <span className="tabular-nums">{data.appsPerDay[data.appsPerDay.length - 1] ?? 0} today</span>
        </div>
      </Card>

      <Card title="Time to first interview" dense>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold vscode-text-primary tabular-nums">
            {data.ttfi.medianDays}
          </span>
          <span className="text-[12px] vscode-text-tertiary">d median</span>
          {data.ttfi.trendPct !== 0 && (
            <span
              className="ml-auto inline-flex items-center gap-0.5 text-[11px] font-medium"
              style={{ color: trendDown ? '#047857' : trendUp ? '#B45309' : 'var(--vscode-text-tertiary)' }}
            >
              {trendDown ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
              {Math.abs(data.ttfi.trendPct)}%
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[11px] vscode-text-tertiary">
          based on {data.ttfi.sampleSize} applications that reached interview
        </p>
      </Card>

      <Card title="Channel mix" dense>
        <ChannelMixBar segments={data.channelMix} />
      </Card>

      <Card title="Recent activity" dense>
        {data.activity.length === 0 ? (
          <p className="text-[12px] vscode-text-tertiary">No recent changes recorded.</p>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--vscode-border-primary)' }}>
            {data.activity.map((row) => (
              <li key={row.id} className="flex items-start gap-2 py-1.5">
                <History className="mt-0.5 h-3 w-3 shrink-0 vscode-text-tertiary" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] vscode-text-primary">{formatActivity(row)}</p>
                  <p className="text-[11px] vscode-text-tertiary">{formatRelative(row.created_at)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="flex items-center gap-3 pt-1 text-[11px] vscode-text-tertiary">
        <Link to={objectHistoryUrl('requisitions')} className="hover:underline">
          View history
        </Link>
        {onViewTimeline && (
          <>
            <span aria-hidden>·</span>
            <button type="button" onClick={onViewTimeline} className="hover:underline">
              View full timeline
            </button>
          </>
        )}
      </div>
    </div>
  );
}
